import { useState } from 'react';
import type { ProcessArtifactDto } from '@api/processes';
import { useEditorContext } from '../useEditorContext';

const EMPTY: readonly ProcessArtifactDto[] = [];

const isImageArtifact = (a: ProcessArtifactDto): boolean =>
  a.contentType.startsWith('image/');

/** Same artifact on screen — id + presigned URL both unchanged. */
const sameImage = (
  a: ProcessArtifactDto | null,
  b: ProcessArtifactDto | null,
): boolean =>
  a === b ||
  (!!a && !!b && a.artifactId === b.artifactId && a.downloadUrl === b.downloadUrl);

/** Same list content — used so a fresh-but-equal array ref doesn't churn. */
const sameList = (
  a: readonly ProcessArtifactDto[],
  b: readonly ProcessArtifactDto[],
): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every(
    (x, i) => x.artifactId === b[i].artifactId && x.downloadUrl === b[i].downloadUrl,
  );
};

/**
 * Genuine live-run artifacts for a node — the SSE-streamed list, and ONLY
 * when an active run is tracked. Unlike {@link useNodeLiveArtifacts} this
 * never falls back to the persisted snapshot, so callers can tell "the
 * live run has produced an image" apart from "the persisted snapshot
 * happens to contain one". That distinction is what keeps the preview
 * from regressing to an older persisted artifact during the window where
 * {@code doActivateRun} has cleared the live map (see AIB-7181).
 */
function useGenuineLiveArtifacts(
  nodeId: string,
): readonly ProcessArtifactDto[] {
  const { runStateForNode } = useEditorContext();
  const runState = runStateForNode(nodeId);
  if (runState.source === 'live') {
    return runState.live?.artifacts ?? EMPTY;
  }
  return EMPTY;
}

/**
 * Resolve the image artifact to show for a generation node as a
 * forward-only selection: once an image is on screen it is never replaced
 * by a *different, older* persisted artifact. Adopt a fresh live image,
 * a same-artifact URL refresh, or — when no image has ever been held — the
 * persisted one. Everything else holds the previously shown image.
 *
 * <p>This is gap-proof without a transient "running" flag: on a single-
 * node run, clearing the live map flips {@code runStateForNode} back to
 * the persisted snapshot, which can be older than what the user was just
 * looking at. Comparing by {@code artifactId} means that stale snapshot
 * can't win until the live run produces its own image.</p>
 *
 * <p>The held value is adjusted during render (not in an effect) so the
 * returned artifact is always current — no one-frame lag. The set is
 * content-guarded so a fresh-but-equal artifact object can't loop.</p>
 */
export function useHeldNodeImage(
  nodeId: string,
  latestArtifacts: readonly ProcessArtifactDto[] | undefined,
): ProcessArtifactDto | null {
  const liveImage = useGenuineLiveArtifacts(nodeId).find(isImageArtifact) ?? null;
  const persistedImage = latestArtifacts?.find(isImageArtifact) ?? null;

  const [held, setHeld] = useState<ProcessArtifactDto | null>(
    liveImage ?? persistedImage,
  );

  let chosen: ProcessArtifactDto | null;
  if (liveImage) {
    chosen = liveImage;
  } else if (
    persistedImage &&
    (held === null || persistedImage.artifactId === held.artifactId)
  ) {
    chosen = persistedImage;
  } else {
    chosen = held;
  }

  if (!sameImage(chosen, held)) setHeld(chosen);
  return sameImage(chosen, held) ? held : chosen;
}

/**
 * Forward-only artifact list for a node's PREVIEW strip (3D-gen). Holds
 * the last concretely displayed list while a run is in flight and the
 * live stream has not produced anything yet, so the strip neither flashes
 * to a stale persisted snapshot nor blanks to empty mid-run. When idle the
 * persisted snapshot is authoritative; a non-empty live list always wins.
 */
export function useHeldNodeArtifacts(
  nodeId: string,
  latestArtifacts: readonly ProcessArtifactDto[] | undefined,
): readonly ProcessArtifactDto[] {
  const { isNodeGenerating } = useEditorContext();
  const liveArtifacts = useGenuineLiveArtifacts(nodeId);
  const generating = isNodeGenerating(nodeId);
  const persisted = latestArtifacts ?? EMPTY;

  const [held, setHeld] = useState<readonly ProcessArtifactDto[]>(
    liveArtifacts.length > 0 ? liveArtifacts : persisted,
  );

  // The last concretely-shown list to memorialise — never an empty
  // mid-run state. `null` means "keep holding" (generating, no live yet).
  let candidate: readonly ProcessArtifactDto[] | null;
  if (liveArtifacts.length > 0) {
    candidate = liveArtifacts;
  } else if (!generating) {
    candidate = persisted;
  } else {
    candidate = null;
  }
  if (candidate !== null && !sameList(candidate, held)) setHeld(candidate);

  if (liveArtifacts.length > 0) return liveArtifacts;
  if (generating) return held;
  return persisted;
}
